import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Anchor, ChevronLeft, ChevronRight, Dices, Gift, Headphones, Home,
  Menu, Phone, PlayCircle, Plus, Rocket, Search, ShieldCheck, Target, Ticket,
  Trophy, Wallet, X,
} from 'lucide-react';
import Logo from '../../components/Logo';
import UserDropdown from '../../components/UserDropdown';
import AnnouncementMarquee from '../../components/AnnouncementMarquee';
import AuthModal from '../../components/AuthModal';
import CallbackModal from '../../components/CallbackModal';
import GameSearch from '../../components/GameSearch';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import MeritFooter from './MeritFooter';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { buildLiveChatConfig, openLiveChat } from '../../components/liveChatConfig';
import { useHiddenCategories } from '../../lib/gameCategories';
import {
  bottomNavIcon, pathOf, splitBottomNav, useBottomNavItems,
} from '../../lib/bottomNav';
import { useTopNavLinks } from '../../lib/topNav';
import { renderSettingText } from '../../lib/textTemplate';
import client from '../../lib/api';
import MediaThumb from '../../components/MediaThumb';
import RailLiveSubmenu from '../../components/RailLiveSubmenu';
import './merit.css';
import { adminShortcutVisible } from '../../lib/adminDomain';
// Panelden yazılan CSS'in tutunacağı sabit kimlikler (bkz. lib/domHooks.js):
// kimlik şemaya gömülmez, ortak sabitten okunur.
import { SHELL_IDS } from '../../lib/domHooks';

// Sol ray ve üst kategori barı aynı kaynaktan beslenir; kategori kapatıldığında
// ikisinden birden düşer.
// `tint` yalnızca sol raydaki ikonu renklendirir: referans arayüzde her kategori
// kendi rengiyle işaretlidir, aktif olan altın zemine oturur.
const NAV_ITEMS = [
  { to: '/', label: 'Anasayfa', icon: Home, end: true, tint: '#bc9b6a' },
  { to: '/spor', label: 'Spor', icon: Trophy, sports: true, tint: '#0fb33e' },
  { to: '/slotlar', label: 'Slotlar', icon: Dices, category: 'slot', tint: '#8a2cff' },
  { to: '/canli-casino', label: 'Canlı Casino', icon: PlayCircle, category: 'live-casino', tint: '#c1124a', liveTabs: true },
  { to: '/crash', label: 'Hızlı Oyunlar', icon: Rocket, category: 'crash', tint: '#f91221' },
  { to: '/promosyonlar', label: 'Bonuslar', icon: Gift, tint: '#bc9b6a' },
  // Çark ve çapa kategori değildir; kendi bayrakları açıkken görünürler ve
  // panelden footer menüsüne de eklenmişlerse buradaki kopya düşer (aşağıda).
  { to: '/cark', label: 'Şans Çarkı', icon: Target, flag: 'wheel_enabled', optional: true, tint: '#e6b34a' },
  { to: '/capa', label: 'Çapa', icon: Anchor, flag: 'anchor_enabled', optional: true, tint: '#2f9fd8' },
  // Çekiliş kategori değildir; yayında bir çekiliş varken görünür
  // (``raffle_active`` çalışma anındaki durumdur, bkz. CLAUDE.md).
  { to: '/cekilis', label: 'Çekiliş', icon: Ticket, flag: 'raffle_active', optional: true, tint: '#34d399' },
  { to: '/etkinlik', label: 'Tahmin', icon: Target, flag: 'prediction_active', optional: true, tint: '#60a5fa' },
];

// Kategorilerin ardındaki kısayollar (vitrin ve kampanya sayfaları) sabit
// kodlu değildir: yönetim panelinde Footer Yönetimi'nden "Üst menüde göster"
// işaretlenen menü gruplarının bağlantılarından gelir (lib/topNav.js).

const MeritLayout = ({ children }) => {
  const { user, balance, bonusBalance } = useAuth();
  const settings = useSettings();
  // Panel başka bir alan adına taşındıysa kısayol çizilmez: adresi
  // bilmediğimiz için bağlantı kurulamaz ve panelin varlığını
  // duyurmanın da anlamı yoktur (bkz. lib/adminDomain.js).
  const showAdminLink = adminShortcutVisible(settings);
  const nav = useNavigate();
  const location = useLocation();
  // Üstteki hızlı arama kutusundan seçilen oyun doğrudan açılabilsin diye
  // kabuk kendi başlatıcısını taşır.
  const launcher = useGameLauncher();

  const [railOpen, setRailOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidePanelGames, setSidePanelGames] = useState([]);
  const hiddenCats = useHiddenCategories();
  const sidePanelGameKey = (settings.merit_side_panel_games || []).join(',');

  // Kategori barı sayfaya sığıyorsa kaydırma okları gizlenir. Mobilde ok yok;
  // orada kaydırılabildiğini uçlardaki solma anlatır, o yüzden hangi yönde yer
  // kaldığı da izlenir.
  const subnavRef = useRef(null);
  const [subnavOverflow, setSubnavOverflow] = useState(false);
  const [subnavEdges, setSubnavEdges] = useState({ left: false, right: false });

  const callbackEnabled = settings.callback_enabled !== false;
  // Raydaki "Canlı Destek" rozeti widget'ın kendisiyle aynı koşula bağlıdır:
  // panelden kapatıldığında (ya da lisans girilmediğinde) widget hiç yüklenmez,
  // rozet de görünmemeli — yoksa tıklanacak bir destek varmış gibi durur.
  const liveChatEnabled = useMemo(
    () => Boolean(buildLiveChatConfig(settings, user)),
    [settings, user],
  );

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('reset_token')) {
      setAuthTab('reset');
      setAuthOpen(true);
    }
  }, []);

  useEffect(() => {
    if (location.state?.auth !== 'login' || user) return;
    setAuthTab('login');
    setAuthOpen(true);
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, nav, user]);

  // Sayfa değişince mobil ray kapanır.
  useEffect(() => { setRailOpen(false); }, [location.pathname]);

  // Yan panel seçimi ayar kaydında yalnızca oyun kimliklerini tutar; bu uç
  // güncel ad, görsel ve oynanabilirlik bilgilerini katalogdan çözer.
  useEffect(() => {
    let active = true;
    client.get('/games/merit-side-panel')
      .then(({ data }) => {
        if (active) setSidePanelGames(data.items || []);
      })
      .catch(() => { if (active) setSidePanelGames([]); });
    return () => { active = false; };
  }, [sidePanelGameKey]);

  // Ray mobilde tam ekran bir çekmecedir; açıkken arkadaki sayfa kaymasın.
  useEffect(() => {
    if (!railOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [railOpen]);

  // Kategorilerin ardındaki kısayollar footer menüsünden gelir; kapalı bölüme
  // (spor, kategori, şans çarkı, çapa) giden bağlantıları hook'un kendisi ayıklar.
  const extraNav = useTopNavLinks();

  // Aynı adres hem kabuk listesinde hem panelde işaretliyse iki kez çizilirdi;
  // yöneticinin yazdığı etiket kazanır, kabuğun kopyası düşer.
  const shortcutPaths = useMemo(() => new Set(
    extraNav.filter((item) => !item.external).map((item) => pathOf(item.to)),
  ), [extraNav]);

  const visibleNav = useMemo(() => NAV_ITEMS.filter((item) => {
    if (item.sports) return settings.settingsLoaded && settings.sports_enabled !== false;
    if (item.optional && shortcutPaths.has(item.to)) return false;
    // Bayraklar tek tek okunur: ``settings`` nesnesinin tamamını bağımlılığa
    // koymak listeyi her renderda yeniden üretir ve ölçüm efektlerini
    // (bar taşması, aktif madde) boşuna tetiklerdi — context değeri her
    // yoklamada yeni bir nesnedir.
    if (item.flag === 'wheel_enabled') return settings.wheel_enabled === true;
    if (item.flag === 'anchor_enabled') return settings.anchor_enabled === true;
    if (item.flag === 'raffle_active') return settings.raffle_active === true;
    if (item.flag === 'prediction_active') return settings.prediction_active === true;
    return !item.category || !hiddenCats.includes(item.category);
  }), [
    hiddenCats, shortcutPaths, settings.settingsLoaded, settings.sports_enabled,
    settings.wheel_enabled, settings.anchor_enabled, settings.raffle_active, settings.prediction_active,
  ]);

  // Oklar yalnızca bar taşarken anlamlı; genişlik hem pencere boyutuyla hem de
  // görünür kategori sayısıyla değiştiği için ikisi de izlenir.
  const measureSubnav = useCallback(() => {
    const el = subnavRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setSubnavOverflow(max > 1);
    setSubnavEdges({ left: el.scrollLeft > 4, right: max > 4 && el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    measureSubnav();
    const el = subnavRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureSubnav);
      return () => window.removeEventListener('resize', measureSubnav);
    }
    const observer = new ResizeObserver(measureSubnav);
    observer.observe(el);
    // İçerik genişliği yazı tipi yüklenmesiyle de değişebilir.
    Array.from(el.children).forEach((child) => observer.observe(child));
    el.addEventListener('scroll', measureSubnav, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', measureSubnav);
    };
  }, [measureSubnav, visibleNav]);

  // Aktif kategori kaydırma alanının dışında kalabiliyordu; sayfa açıldığında
  // görünür alanın ortasına getirilir. `scrollIntoView` sayfayı da kaydırdığı
  // için yalnızca yatay konum elle ayarlanır.
  useEffect(() => {
    const el = subnavRef.current;
    const active = el?.querySelector('.merit-subnav-item.active');
    if (!el || !active) return;
    const target = active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [location.pathname, visibleNav]);

  // Alt bardaki gezinme yuvaları panelden gelir; ortadaki giriş/para yatır
  // düğmesi listeye girmez, oturuma göre kendiliğinden değişir.
  const bottomNav = useBottomNavItems();
  const { left: bottomLeft, right: bottomRight } = splitBottomNav(bottomNav);

  const openAuth = (tab) => { setAuthTab(tab); setAuthOpen(true); };

  // Panelden gelen yuva. "menu" tipi bir adrese gitmez, yan menüyü açar —
  // mobilde çekmeceye başka giriş olmadığı için panel bu yuvayı silmemeyi
  // ayrıca hatırlatır.
  const renderBottomItem = (item, index) => {
    const Icon = bottomNavIcon(item.icon);
    const key = `${item.type}-${item.url}-${index}`;
    if (item.type === 'menu') {
      return (
        <button
          key={key}
          type="button"
          className={`merit-bottom-item ${railOpen ? 'active' : ''}`}
          onClick={() => setRailOpen((open) => !open)}
          aria-expanded={railOpen}
        >
          <Icon size={18} />
          <span>{item.label}</span>
        </button>
      );
    }
    return (
      <NavLink
        key={key}
        to={item.url}
        end={item.url === '/'}
        className={({ isActive }) => `merit-bottom-item ${isActive ? 'active' : ''}`}
      >
        <Icon size={18} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  // Duyuru bandı panelde tanımlı üst bilgi metninden beslenir.
  const announcement = renderSettingText(settings.top_bar_message || '', settings).trim();

  const scrollSubnav = (direction) => {
    subnavRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  return (
    <div className="merit-shell">
      <aside className={`merit-rail ${railOpen ? 'is-open' : ''}`}>
        <button
          type="button"
          className="merit-rail-btn"
          onClick={() => setRailOpen((open) => !open)}
          aria-label={railOpen ? 'Menüyü daralt' : 'Menüyü genişlet'}
          aria-expanded={railOpen}
        >
          {railOpen ? <X size={20} /> : <Menu size={20} />}
          <span className="merit-rail-label">Menü</span>
        </button>

        <div className="merit-rail-scroll">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `merit-rail-btn ${isActive ? 'active' : ''}`}
              title={item.label}
            >
              {({ isActive }) => (
                <>
                  <item.icon size={20} style={isActive ? undefined : { color: item.tint }} />
                  <span className="merit-rail-label">{item.label}</span>
                </>
              )}
            </NavLink>
          )).map((link, i) => (visibleNav[i].liveTabs ? (
            // Canlı Casino'nun alt menüsü: masa sekmeleri, açılır/kapanır.
            <RailLiveSubmenu key={link.key} prefix="merit" onNavigate={() => setRailOpen(false)}>
              {link}
            </RailLiveSubmenu>
          ) : link))}
          {/* Panelden gelen kısayollar dış adres de olabilir; o durumda
              yönlendirici değil düz bağlantı kullanılır. */}
          {extraNav.map((item) => (item.external ? (
            <a
              key={item.to}
              href={item.to}
              target={item.newTab ? '_blank' : undefined}
              rel={item.newTab ? 'noreferrer' : undefined}
              className="merit-rail-btn"
              title={item.label}
            >
              <item.icon size={20} />
              <span className="merit-rail-label">{item.label}</span>
            </a>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              target={item.newTab ? '_blank' : undefined}
              className={({ isActive }) => `merit-rail-btn ${isActive ? 'active' : ''}`}
              title={item.label}
            >
              <item.icon size={20} />
              <span className="merit-rail-label">{item.label}</span>
            </NavLink>
          )))}
          {user?.is_admin && showAdminLink && (
            <NavLink to="/admin" className="merit-rail-btn" title="Yönetim Paneli">
              <ShieldCheck size={20} />
              <span className="merit-rail-label">Yönetim Paneli</span>
            </NavLink>
          )}
          {sidePanelGames.length > 0 && (
            <section className="merit-rail-games" aria-labelledby="merit-rail-games-title">
              <h2 id="merit-rail-games-title">Oyunlar</h2>
              {sidePanelGames.map((game) => (
                <button
                  key={game.id}
                  type="button"
                  className="merit-rail-game"
                  title={game.name}
                  onClick={() => {
                    setRailOpen(false);
                    launcher.play({ ...game, img: game.image });
                  }}
                >
                  {game.image ? (
                    <MediaThumb src={game.image} />
                  ) : (
                    <span className="merit-rail-game-empty"><Dices size={18} /></span>
                  )}
                  <span className="merit-rail-game-copy">
                    <strong>{game.name}</strong>
                    <small>{game.provider}</small>
                  </span>
                </button>
              ))}
            </section>
          )}
        </div>

        {/* İkisi de kapalıysa ray dibinde boş bir şerit kalmasın. */}
        {(callbackEnabled || liveChatEnabled) && (
          <div className="merit-rail-foot">
            {callbackEnabled && (
              <button
                type="button"
                className="merit-rail-chip"
                style={{ background: 'var(--pusu-card)' }}
                onClick={() => setCallbackOpen(true)}
                title="Beni Ara"
              >
                <Phone size={16} />
                <span className="merit-rail-label">Beni Ara</span>
              </button>
            )}
            {liveChatEnabled && (
              <button
                type="button"
                className="merit-rail-chip"
                style={{ background: '#22c55e' }}
                onClick={() => openLiveChat()}
                title="Canlı Destek"
              >
                <Headphones size={16} />
                <span className="merit-rail-label">Canlı Destek</span>
              </button>
            )}
          </div>
        )}
      </aside>

      {railOpen && (
        <div
          className="merit-rail-backdrop"
          role="presentation"
          onClick={() => setRailOpen(false)}
        />
      )}

      <div className="merit-main">
        {/* Bant klasik kabuktaki gibi kabuğun en üstünde, header'ın da
            üstünde durur: kayar ve kapatılamaz. Duyuru çoğu kurulumda
            sitenin sıradaki adresini taşır — tek satıra sığmadığında
            kesiliyor, kapatılan bant ise bir daha hiç görünmüyordu. */}
        {announcement && (
          <div className="merit-notice">
            <div className="announcement-track merit-notice-track">
              <AnnouncementMarquee text={announcement} />
            </div>
          </div>
        )}

        <header id={SHELL_IDS.header} className="merit-header">
          <Logo />

          <div className="merit-header-links">
            <button type="button" className="merit-header-link" onClick={() => setSearchOpen(true)}>
              <Search size={16} /> Hızlı Ara
            </button>
            <button
              type="button"
              className="merit-header-link is-accent"
              onClick={() => nav('/promosyonlar')}
            >
              <Gift size={16} /> Bonus Talep
            </button>
            <button
              type="button"
              className="merit-header-link"
              onClick={() => nav('/promosyon-kodu')}
            >
              <Ticket size={16} /> Promo Kod
            </button>
          </div>

          <div className="merit-header-actions">
            {user ? (
              <>
                <button
                  type="button"
                  className="merit-btn merit-btn-accent merit-btn-deposit"
                  onClick={() => nav('/hesap/para-yatir')}
                  aria-label="Para Yatır"
                >
                  <Plus size={16} />
                  <span className="merit-btn-text">Para Yatır</span>
                </button>
                {/* Bakiye mobilde de görünür; dar ekranda etiketi CSS gizler. */}
                <div className="merit-balance">
                  <Wallet size={16} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="merit-balance-body">
                    <span className="merit-balance-label">Bakiye</span>
                    <span className="merit-balance-value">{Number(balance).toFixed(2)} ₺</span>
                  </span>
                </div>
                {/* Bonus kutusu yalnızca geniş ekranda; görünürlüğü CSS yönetir.
                    Tailwind'in `hidden` sınıfı burada `.merit-balance`
                    display'ine yeniliyor ve kutu mobilde de çıkıyordu. */}
                <div className="merit-balance merit-balance-bonus">
                  <Gift size={16} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="merit-balance-body">
                    <span className="merit-balance-label">Bonus</span>
                    <span className="merit-balance-value">{Number(bonusBalance).toFixed(2)} ₺</span>
                  </span>
                </div>
                <UserDropdown />
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="merit-btn merit-btn-ghost"
                  onClick={() => openAuth('login')}
                >
                  Giriş Yap
                </button>
                {settings.registration_open !== false && (
                  <button
                    type="button"
                    className="merit-btn merit-btn-accent"
                    onClick={() => openAuth('register')}
                  >
                    Kayıt Ol
                  </button>
                )}
              </>
            )}
          </div>
        </header>

        {/* Kategori barı referans arayüzde header'ın ALTINDADIR ve sayfayla
            birlikte yukarı kayar; sabit kalan yalnızca header'dır. */}
        <nav
          id={SHELL_IDS.nav}
          className={`merit-subnav ${subnavEdges.left ? 'has-left' : ''} ${subnavEdges.right ? 'has-right' : ''}`}
          aria-label="Kategoriler"
        >
          {subnavOverflow && (
            <button
              type="button"
              className="merit-subnav-arrow"
              onClick={() => scrollSubnav(-1)}
              aria-label="Sola kaydır"
            >
              <ChevronLeft size={14} />
            </button>
          )}
          <div className="merit-subnav-scroll" id="merit-subnav-scroll" ref={subnavRef}>
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `merit-subnav-item ${isActive ? 'active' : ''}`}
              >
                <item.icon size={14} /> {item.label}
              </NavLink>
            ))}
            {extraNav.map((item) => (item.external ? (
              <a
                key={item.to}
                href={item.to}
                target={item.newTab ? '_blank' : undefined}
                rel={item.newTab ? 'noreferrer' : undefined}
                className="merit-subnav-item"
              >
                <item.icon size={14} /> {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                target={item.newTab ? '_blank' : undefined}
                className={({ isActive }) => `merit-subnav-item ${isActive ? 'active' : ''}`}
              >
                <item.icon size={14} /> {item.label}
              </NavLink>
            )))}
          </div>
          {subnavOverflow && (
            <button
              type="button"
              className="merit-subnav-arrow"
              onClick={() => scrollSubnav(1)}
              aria-label="Sağa kaydır"
            >
              <ChevronRight size={14} />
            </button>
          )}
        </nav>

        <div id={SHELL_IDS.main} className="merit-content">{children}</div>

        <MeritFooter />
      </div>

      {/* Alt bar beş yuvadır: panelden gelen dört yuva ile ortadaki sabit
          giriş/para yatır düğmesi. Daha fazlası 360px'lik ekranda etiketleri
          okunmaz hâle getiriyordu. */}
      <nav id={SHELL_IDS.bottomBar} className="app-bottom-bar merit-bottom-bar" aria-label="Alt menü">
        {bottomLeft.map(renderBottomItem)}
        {user ? (
          <NavLink to="/hesap/para-yatir" className="merit-bottom-item is-wallet">
            <Wallet size={20} />
            <span>Para Yatır</span>
          </NavLink>
        ) : (
          <button
            type="button"
            className="merit-bottom-item is-wallet"
            onClick={() => openAuth('login')}
          >
            <Wallet size={20} />
            <span>Giriş Yap</span>
          </button>
        )}
        {bottomRight.map(renderBottomItem)}
      </nav>

      {searchOpen && (
        <div
          className="fixed inset-0 z-[70] flex justify-center px-4 pt-24"
          style={{ background: 'rgba(4, 10, 28, 0.75)' }}
          onClick={() => setSearchOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl"
            onClick={(event) => event.stopPropagation()}
            role="presentation"
          >
            <GameSearch onPlay={(game) => { setSearchOpen(false); launcher.play(game); }} />
          </div>
        </div>
      )}

      <GameChoiceModal
        game={launcher.choice}
        user={launcher.user}
        balance={launcher.balance}
        bonusBalance={launcher.bonusBalance}
        demoAvailable={launcher.demoAvailable}
        onClose={launcher.closeChoice}
        onReal={launcher.startReal}
        onDemo={launcher.startDemo}
        onLogin={() => launcher.openAuth('login')}
        onRegister={() => launcher.openAuth('register')}
      />
      {launcher.activeGame && (
        <GameModal
          key={`${launcher.activeGame.id}-${launcher.mode}`}
          game={launcher.activeGame}
          mode={launcher.mode}
          onClose={launcher.closeGame}
        />
      )}

      <AuthModal
        open={authOpen || !!launcher.authTab}
        onClose={() => { setAuthOpen(false); launcher.setAuthTab(null); }}
        defaultTab={launcher.authTab || authTab}
        registrationOpen={settings.registration_open !== false}
      />
      <CallbackModal open={callbackEnabled && callbackOpen} onClose={() => setCallbackOpen(false)} />
    </div>
  );
};

export default MeritLayout;
